/**
 * CCTVRulesEditor — Phase D AI detection rules CRUD.
 *
 * Lets admins define which YOLO detection events from the worker sidecar
 * should fire alerts (and optionally incidents) per camera.
 *
 * Supports 5 rule kinds (matches backend `cctv_ai.RULE_TYPES`):
 *   • after_hours_motion    — any motion outside start/end_hour window
 *   • person_threshold       — N+ people detected
 *   • restricted_zone        — anything entering polygon
 *   • line_cross             — cross a line segment
 *   • unauthorized_label     — specific label appears (e.g. weapon, vehicle)
 *
 * Also exposes a "Rotate detection key" button per device — the raw key is
 * shown ONCE so the operator can paste it into the worker's `.env`.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Brain, Plus, Edit2, Trash2, KeyRound, Copy, AlertTriangle, Shield,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

const RULE_TYPES = [
  { value: 'after_hours_motion', label: 'After-hours motion', fields: ['start_hour', 'end_hour'] },
  { value: 'person_threshold', label: 'Person threshold (N+ people)', fields: ['min_count'] },
  { value: 'restricted_zone', label: 'Restricted zone (polygon)', fields: ['zone_polygons'] },
  { value: 'line_cross', label: 'Line crossing', fields: ['line_segments'] },
  { value: 'unauthorized_label', label: 'Unauthorized label (e.g. weapon)', fields: ['unauthorized_label'] },
];

const DEFAULT_FORM = {
  device_id: '',
  rule_type: 'person_threshold',
  label: '',
  enabled: true,
  severity: 'warning',
  start_hour: 22,
  end_hour: 6,
  min_count: 3,
  zone_polygons: '',
  line_segments: '',
  unauthorized_label: '',
  confidence_min: 0.5,
  cooldown_seconds: 60,
  notify_security: true,
  create_incident: false,
};

const SEV_COLOR = {
  info: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  warning: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  critical: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
};

const RuleRow = ({ rule, onEdit, onDelete, onToggle, devices }) => {
  const device = devices.find(d => d.id === rule.device_id);
  const meta = RULE_TYPES.find(r => r.value === rule.rule_type);
  return (
    <Card data-testid={`cctv-rule-${rule.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className={`${SEV_COLOR[rule.severity] || SEV_COLOR.warning} border`}>{rule.severity}</Badge>
              <span className="text-sm font-medium truncate">{rule.label || meta?.label || rule.rule_type}</span>
              {!rule.enabled && <Badge variant="outline" className="text-muted-foreground">disabled</Badge>}
              {rule.create_incident && <Badge className="bg-rose-500/15 text-rose-600 dark:text-rose-300 border-rose-500/40 border gap-1"><AlertTriangle className="h-3 w-3" /> incident</Badge>}
              {rule.notify_security && <Badge className="bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/40 border gap-1"><Shield className="h-3 w-3" /> notify</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">
              {meta?.label || rule.rule_type} · camera <span className="font-medium text-foreground">{device?.name || rule.device_id?.slice(-6)}</span>
              {' · '}min conf {Math.round((rule.confidence_min || 0) * 100)}%
              {' · '}cooldown {rule.cooldown_seconds || 60}s
              {rule.rule_type === 'person_threshold' && ` · ${rule.min_count}+ people`}
              {rule.rule_type === 'after_hours_motion' && ` · ${rule.start_hour}:00→${rule.end_hour}:00`}
              {rule.rule_type === 'unauthorized_label' && ` · label "${rule.unauthorized_label}"`}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Switch checked={rule.enabled} onCheckedChange={(c) => onToggle(rule, c)} data-testid={`cctv-rule-toggle-${rule.id}`} />
            <Button size="sm" variant="ghost" onClick={() => onEdit(rule)} data-testid={`cctv-rule-edit-${rule.id}`}>
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(rule)} data-testid={`cctv-rule-delete-${rule.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const CCTVRulesEditor = ({ devices = [] }) => {
  const qc = useQueryClient();
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [editing, setEditing] = useState(null);   // rule being edited or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [keyShown, setKeyShown] = useState(null);

  const { data: rules = [] } = useQuery({
    queryKey: ['cctv-rules', selectedDevice],
    queryFn: async () => {
      const url = selectedDevice === 'all'
        ? `${API}/api/cctv/detection/rules`
        : `${API}/api/cctv/detection/rules?device_id=${selectedDevice}`;
      const res = await fetch(url, { headers: authH() });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      // Parse JSON-encoded polygon/segment text fields
      ['zone_polygons', 'line_segments'].forEach((k) => {
        if (typeof body[k] === 'string') {
          try { body[k] = body[k].trim() ? JSON.parse(body[k]) : null; }
          catch { throw new Error(`${k} must be valid JSON`); }
        }
      });
      const url = editing
        ? `${API}/api/cctv/detection/rules/${editing.id}`
        : `${API}/api/cctv/detection/rules`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authH(), body: JSON.stringify(body) });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || 'Save failed';
        throw new Error(detail);
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success(editing ? 'Rule updated' : 'Rule created');
      setShowForm(false); setEditing(null); setForm(DEFAULT_FORM);
      qc.invalidateQueries({ queryKey: ['cctv-rules'] });
    },
    onError: (e) => toast.error(e.message || 'Failed'),
  });

  const del = useMutation({
    mutationFn: async (rule) => {
      const res = await fetch(`${API}/api/cctv/detection/rules/${rule.id}`, { method: 'DELETE', headers: authH() });
      if (!res.ok) throw new Error('Delete failed');
      return res.json();
    },
    onSuccess: () => { toast.success('Rule deleted'); qc.invalidateQueries({ queryKey: ['cctv-rules'] }); },
    onError: (e) => toast.error(e.message),
  });

  const toggleRule = useMutation({
    mutationFn: async ({ rule, enabled }) => {
      const res = await fetch(`${API}/api/cctv/detection/rules/${rule.id}`, {
        method: 'PUT', headers: authH(), body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cctv-rules'] }),
  });

  const rotateKey = useMutation({
    mutationFn: async (deviceId) => {
      const res = await fetch(`${API}/api/cctv/devices/${deviceId}/worker-key/rotate`, {
        method: 'POST', headers: authH(),
      });
      if (!res.ok) throw new Error('Rotation failed');
      return { ...(await res.json()), device_id: deviceId };
    },
    onSuccess: (d) => {
      const dev = devices.find(x => x.id === d.device_id);
      setKeyShown({ device_name: dev?.name || d.device_id, key: d.detection_key });
    },
    onError: (e) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...DEFAULT_FORM, device_id: selectedDevice !== 'all' ? selectedDevice : (devices[0]?.id || '') });
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      ...DEFAULT_FORM, ...rule,
      zone_polygons: rule.zone_polygons ? JSON.stringify(rule.zone_polygons) : '',
      line_segments: rule.line_segments ? JSON.stringify(rule.line_segments) : '',
    });
    setShowForm(true);
  };

  const activeMeta = RULE_TYPES.find(r => r.value === form.rule_type);

  return (
    <div className="space-y-4" data-testid="cctv-rules-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold inline-flex items-center gap-2">
            <Brain className="h-5 w-5 text-indigo-500" /> AI Detection Rules
          </h3>
          <p className="text-sm text-muted-foreground">Rules evaluated by the YOLO worker sidecar against each camera frame.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedDevice} onValueChange={setSelectedDevice}>
            <SelectTrigger className="w-56" data-testid="cctv-rules-device-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cameras</SelectItem>
              {devices.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedDevice !== 'all' && (
            <Button size="sm" variant="outline" onClick={() => rotateKey.mutate(selectedDevice)} data-testid="cctv-rotate-key">
              <KeyRound className="h-4 w-4 mr-1" /> Rotate worker key
            </Button>
          )}
          <Button size="sm" onClick={openCreate} disabled={!devices.length} data-testid="cctv-rules-add">
            <Plus className="h-4 w-4 mr-1" /> New rule
          </Button>
        </div>
      </div>

      <div className="grid gap-2">
        {rules.map(r => (
          <RuleRow key={r.id} rule={r} devices={devices}
                   onEdit={openEdit}
                   onDelete={(rule) => window.confirm('Delete this rule?') && del.mutate(rule)}
                   onToggle={(rule, enabled) => toggleRule.mutate({ rule, enabled })} />
        ))}
        {rules.length === 0 && (
          <div className="text-muted-foreground text-center py-10 border border-dashed rounded">
            No rules yet — click <strong>New rule</strong> to add one.
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit detection rule' : 'New detection rule'}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Worker calls <code className="text-emerald-300">POST /devices/&#123;id&#125;/detection</code>; matching rules fire alerts.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Camera</Label>
              <Select value={form.device_id} onValueChange={(v) => setForm({ ...form, device_id: v })}>
                <SelectTrigger className="bg-slate-800/60 border-slate-700" data-testid="cctv-rule-device"><SelectValue placeholder="Select camera…" /></SelectTrigger>
                <SelectContent>{devices.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Rule type</Label>
              <Select value={form.rule_type} onValueChange={(v) => setForm({ ...form, rule_type: v })}>
                <SelectTrigger className="bg-slate-800/60 border-slate-700" data-testid="cctv-rule-type"><SelectValue /></SelectTrigger>
                <SelectContent>{RULE_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="bg-slate-800/60 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Label (optional, human-readable)</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. After-hours main lobby" className="bg-slate-800/60 border-slate-700" data-testid="cctv-rule-label" />
            </div>

            {/* Type-specific inputs */}
            {activeMeta?.fields.includes('start_hour') && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Start hour (0–23)</Label>
                  <Input type="number" min={0} max={23} value={form.start_hour}
                          onChange={(e) => setForm({ ...form, start_hour: Number(e.target.value) })}
                          className="bg-slate-800/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End hour (0–23)</Label>
                  <Input type="number" min={0} max={23} value={form.end_hour}
                          onChange={(e) => setForm({ ...form, end_hour: Number(e.target.value) })}
                          className="bg-slate-800/60 border-slate-700" />
                </div>
              </>
            )}

            {activeMeta?.fields.includes('min_count') && (
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Min count (alert when N+ persons detected)</Label>
                <Input type="number" min={1} max={100} value={form.min_count}
                        onChange={(e) => setForm({ ...form, min_count: Number(e.target.value) })}
                        className="bg-slate-800/60 border-slate-700" />
              </div>
            )}

            {activeMeta?.fields.includes('zone_polygons') && (
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Zone polygons (JSON — list of point lists, e.g. <code>[[[0.1,0.1],[0.9,0.1],[0.9,0.9],[0.1,0.9]]]</code>)</Label>
                <textarea rows={3} value={form.zone_polygons}
                          onChange={(e) => setForm({ ...form, zone_polygons: e.target.value })}
                          className="w-full bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
                          placeholder='[[[0.1,0.1],[0.9,0.1],[0.9,0.9],[0.1,0.9]]]' />
              </div>
            )}

            {activeMeta?.fields.includes('line_segments') && (
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Line segments (JSON — list of segments, e.g. <code>[[[0,0.5],[1,0.5]]]</code>)</Label>
                <textarea rows={2} value={form.line_segments}
                          onChange={(e) => setForm({ ...form, line_segments: e.target.value })}
                          className="w-full bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
                          placeholder='[[[0,0.5],[1,0.5]]]' />
              </div>
            )}

            {activeMeta?.fields.includes('unauthorized_label') && (
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Unauthorized label (must match exactly what the worker reports)</Label>
                <Input value={form.unauthorized_label || ''} placeholder="weapon / knife / car / etc."
                        onChange={(e) => setForm({ ...form, unauthorized_label: e.target.value })}
                        className="bg-slate-800/60 border-slate-700" />
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-500">Min confidence (0–1)</Label>
              <Input type="number" step="0.05" min={0} max={1} value={form.confidence_min}
                      onChange={(e) => setForm({ ...form, confidence_min: Number(e.target.value) })}
                      className="bg-slate-800/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Cooldown (seconds)</Label>
              <Input type="number" min={1} max={3600} value={form.cooldown_seconds}
                      onChange={(e) => setForm({ ...form, cooldown_seconds: Number(e.target.value) })}
                      className="bg-slate-800/60 border-slate-700" />
            </div>

            <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700 rounded px-3 py-2">
              <Label className="text-xs text-slate-300">Enabled</Label>
              <Switch checked={form.enabled} onCheckedChange={(c) => setForm({ ...form, enabled: c })} />
            </div>
            <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700 rounded px-3 py-2">
              <Label className="text-xs text-slate-300">Notify security team</Label>
              <Switch checked={form.notify_security} onCheckedChange={(c) => setForm({ ...form, notify_security: c })} />
            </div>
            <div className="col-span-2 flex items-center justify-between bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
              <div>
                <Label className="text-xs text-rose-200">Create incident report</Label>
                <p className="text-[11px] text-rose-300/70">Spawns a row in the existing Incidents module — use for critical events only.</p>
              </div>
              <Switch checked={form.create_incident} onCheckedChange={(c) => setForm({ ...form, create_incident: c })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !form.device_id} data-testid="cctv-rule-save">
              {save.isPending ? 'Saving…' : (editing ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key reveal dialog (one-time) */}
      <Dialog open={!!keyShown} onOpenChange={(o) => !o && setKeyShown(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle>Worker detection key · {keyShown?.device_name}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Paste this into the YOLO worker&apos;s <code>.env</code> as <code>CCTV_DETECTION_KEY</code>. Only the SHA-256 hash is stored — it cannot be retrieved later, only rotated again.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-950 border border-slate-800 rounded p-3 font-mono text-emerald-300 text-xs break-all">
            {keyShown?.key}
          </div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText(keyShown?.key || ''); toast.success('Copied'); }}>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button variant="outline" onClick={() => setKeyShown(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CCTVRulesEditor;
